import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPANY_CHAINS, COMPANY_LICENCE_COSTS, formatDate } from '@pk/shared';
import type { CompanyChain, CompanyView, Id } from '@pk/shared';
import { useCities, useCommand, useCompanies } from '../../api/hooks';
import { Btn, Collapsible, ErrorBox, Field, Inline, Sheet, Tag } from '../../components/ui';
import { ResourceIcon } from '../../components/icons';
import { Meta, Row, SheetLayout, Skeleton } from './shared';

const CHAINS = Object.keys(COMPANY_CHAINS) as CompanyChain[];
type Member = { id: Id; name: string; adult: boolean };

function Recipe({ chain }: { chain: CompanyChain }) {
  const { t } = useTranslation();
  const c = COMPANY_CHAINS[chain];
  return (
    <span className="tr-recipe">
      <ResourceIcon k={c.inputResource} size={18} />{c.inputQty}
      <span aria-hidden="true">→</span>
      <ResourceIcon k={c.outputResource} size={18} />{c.outputQty}
      <span className="tr-faint">{t('eco.companies.perMonth')}</span>
    </span>
  );
}

export function CompaniesView({ actor, gold, members }: { actor: Id; gold: number; members: Member[] }) {
  const { t } = useTranslation();
  const companies = useCompanies();
  const cmd = useCommand();
  const [found, setFound] = useState(false);
  const [assign, setAssign] = useState<CompanyView | null>(null);
  if (companies.isLoading) return <Skeleton rows={3} />;
  if (companies.isError || !companies.data) return <ErrorBox error={companies.error} onRetry={() => companies.refetch()} />;
  const all = companies.data.companies;
  const owned = new Set(members.map((m) => m.id));
  const mine = all.filter((c) => owned.has(c.ownerId));
  const others = all.filter((c) => !owned.has(c.ownerId));
  const foundCost = COMPANY_LICENCE_COSTS[1];

  return (
    <section className="tr-section">
      <div className="tr-section-head">
        <p className="tr-help">{t('eco.companies.help', { gold: foundCost })}</p>
        <Btn variant="primary" block onClick={() => setFound(true)}>{t('trade.companies.found')}</Btn>
      </div>

      <h4 className="tr-h">{t('trade.companies.mine')}{mine.length > 0 && <span className="tr-count">{mine.length}</span>}</h4>
      {mine.length === 0 ? (
        <div className="tr-empty">
          <ResourceIcon k="gold" size={40} />
          <strong>{t('trade.companies.none')}</strong>
          <span>{t('eco.companies.emptyHelp')}</span>
        </div>
      ) : (
        <div className="tr-list">
          {mine.map((c) => {
            const next = c.licenceLevel < 3 ? COMPANY_LICENCE_COSTS[c.licenceLevel + 1] : null;
            return (
              <article key={c.id} className="card tr-company">
                <div className="tr-company-top">
                  <div className="tr-row-main">
                    <span className="tr-row-title">{c.name}</span>
                    <span className="tr-row-meta"><Meta items={[t(`trade.chain.${c.chain}`), c.cityName]} /></span>
                  </div>
                  <Tag tone="gold">{t('eco.companies.licence', { n: c.licenceLevel })}</Tag>
                </div>
                <Recipe chain={c.chain} />
                <div className="tr-company-facts">
                  <span>{c.managerName ? t('eco.companies.managedBy', { name: c.managerName }) : <em>{t('eco.companies.noManager')}</em>}</span>
                  <span>{t('eco.companies.wages', { n: c.wages })}</span>
                </div>
                <div className="tr-actions">
                  <Btn small busy={cmd.pending} disabled={next == null}
                    onClick={() => { void cmd.run({ type: 'upgrade_company_licence', actorId: actor, companyId: c.id }); }}>
                    {next == null ? t('eco.companies.topLicence') : t('eco.companies.upgradeFor', { gold: next })}
                  </Btn>
                  <Btn small variant="quiet" onClick={() => setAssign(c)}>{c.managerName ? t('eco.companies.change') : t('trade.companies.assign')}</Btn>
                </div>
              </article>
            );
          })}
          <Inline error={cmd.error} />
        </div>
      )}

      <div className="tr-stack">
        <Collapsible title={t('eco.companies.all')} summary={others.length}>
          {others.length === 0 ? <p className="tr-help">{t('trade.companies.none')}</p> : (
            <div className="tr-list">
              {others.map((c) => (
                <Row key={c.id} title={c.name}
                  meta={<Meta items={[t(`trade.chain.${c.chain}`), c.cityName, t('eco.companies.runBy', { name: c.ownerName }), formatDate(c.foundedDay)]} />} />
              ))}
            </div>
          )}
        </Collapsible>
      </div>

      {found && <FoundSheet actor={actor} gold={gold} onClose={() => setFound(false)} />}
      {assign && <AssignSheet actor={actor} company={assign} adults={members.filter((m) => m.adult)} onClose={() => setAssign(null)} />}
    </section>
  );
}

function FoundSheet({ actor, gold, onClose }: { actor: Id; gold: number; onClose: () => void }) {
  const { t } = useTranslation();
  const cities = useCities();
  const cmd = useCommand();
  const [name, setName] = useState('');
  const [chain, setChain] = useState<CompanyChain>('weaver');
  const [city, setCity] = useState<Id>('');
  const cityList = cities.data?.cities ?? [];
  const cityId = city || cityList[0]?.cityId || '';
  const validName = name.trim().length >= 2 && name.trim().length <= 40;
  const cost = COMPANY_LICENCE_COSTS[1];
  const submit = async () => {
    const res = await cmd.run({ type: 'found_company', actorId: actor, cityId, name: name.trim(), chain });
    if (res.ok) onClose();
  };
  return (
    <Sheet open onClose={onClose} fixed title={t('trade.companies.found')}>
      <SheetLayout foot={
        <>
          <Inline error={cmd.error} />
          <div className="tr-sum">
            <span className="tr-sum-main">{t('eco.companies.foundCost', { gold: cost })}</span>
            <span className={`tr-sum-sub ${gold < cost ? 'warn' : ''}`}>{gold < cost ? t('eco.order.tooPoor', { gold }) : t('eco.companies.guildNeed')}</span>
          </div>
          <Btn variant="primary" block busy={cmd.pending} disabled={!validName || !cityId} onClick={() => { void submit(); }}>{t('trade.companies.foundBtn')}</Btn>
        </>
      }>
        <Field label={t('trade.companies.name')} hint={t('trade.companies.nameHint')}>
          <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <span className="field-label">{t('trade.companies.chain')}</span>
        <div className="tr-chains">
          {CHAINS.map((c) => (
            <button key={c} type="button" className={`tr-chain ${chain === c ? 'on' : ''}`} aria-pressed={chain === c} onClick={() => setChain(c)}>
              <strong>{t(`trade.chain.${c}`)}</strong>
              <Recipe chain={c} />
            </button>
          ))}
        </div>
        <Field label={t('trade.companies.city')}>
          <select className="input" value={cityId} onChange={(e) => setCity(e.target.value)}>
            {cityList.map((c) => <option key={c.cityId} value={c.cityId}>{c.name}</option>)}
          </select>
        </Field>
      </SheetLayout>
    </Sheet>
  );
}

function AssignSheet({ actor, company, adults, onClose }: { actor: Id; company: CompanyView; adults: Member[]; onClose: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [pick, setPick] = useState<Id>(company.managerId ?? '');
  const submit = async () => {
    const res = await cmd.run({ type: 'assign_company_manager', actorId: actor, companyId: company.id, characterId: pick });
    if (res.ok) onClose();
  };
  return (
    <Sheet open onClose={onClose} fixed title={t('eco.companies.assignTitle', { name: company.name })}>
      <SheetLayout foot={
        <>
          <Inline error={cmd.error} />
          <Btn variant="primary" block busy={cmd.pending} disabled={!pick || pick === company.managerId} onClick={() => { void submit(); }}>{t('trade.companies.assign')}</Btn>
        </>
      }>
        <p className="tr-help">{t('eco.companies.assignHelp')}</p>
        {adults.length === 0 ? <p className="tr-help">{t('eco.companies.noAdults')}</p> : (
          <div className="tr-list">
            {adults.map((m) => (
              <button key={m.id} type="button" className={`tr-row tappable tr-pick ${pick === m.id ? 'on' : ''}`} aria-pressed={pick === m.id} onClick={() => setPick(m.id)}>
                <span className="tr-radio" aria-hidden="true" />
                <span className="tr-row-main"><span className="tr-row-title">{m.name}</span>
                  {m.id === company.managerId && <span className="tr-row-meta">{t('eco.companies.current')}</span>}</span>
              </button>
            ))}
          </div>
        )}
      </SheetLayout>
    </Sheet>
  );
}
